import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { creditCardService, CreditCardFilters, CreateCreditCardData } from '../services/creditCardService'
import type { CreditCard } from '@shared/firebase/schema'

// ── Query keys (single source of truth) ─────────────────────────────
export const cardKeys = {
  all:    ['credit-cards'] as const,
  lists:  () => [...cardKeys.all, 'list'] as const,
  list:   (filters: CreditCardFilters) => [...cardKeys.lists(), filters] as const,
  detail: (id: string) => [...cardKeys.all, 'detail', id] as const,
}

// ── Queries ─────────────────────────────────────────────────────────
export function useCreditCards(filters: CreditCardFilters = {}) {
  return useQuery({
    queryKey: cardKeys.list(filters),
    queryFn:  () => creditCardService.getAllCards(filters),
  })
}

export function useCard(id: string | undefined) {
  return useQuery({
    queryKey: cardKeys.detail(id ?? ''),
    queryFn:  () => creditCardService.getCard(id!),
    enabled:  !!id,
  })
}

// ── Mutations ───────────────────────────────────────────────────────
export function useCreateCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCreditCardData) => creditCardService.createCard(data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: cardKeys.all }) },
  })
}

export function useUpdateCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreditCard> }) =>
      creditCardService.updateCard(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: cardKeys.all })
      qc.invalidateQueries({ queryKey: cardKeys.detail(id) })
    },
  })
}

export function useDeleteCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cardId: string) => creditCardService.deleteCard(cardId),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: cardKeys.all }) },
  })
}

export function useToggleFeatured() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ cardId, isFeatured }: { cardId: string; isFeatured: boolean }) =>
      creditCardService.toggleFeaturedStatus(cardId, isFeatured),
    onSuccess: () => { qc.invalidateQueries({ queryKey: cardKeys.all }) },
  })
}
